// skills/pilfering/pilferingData.js
// Pilfering targets and configuration

export const TARGETS = {
    DRIFTER: {
        id: 'drifter',
        name: 'Drifter',
        level: 1,
        xp: 8,
        baseTime: 2000,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 8 }
        ],
        color: '#8b7355'
    },
    FIELDHAND: {
        id: 'fieldhand',
        name: 'Fieldhand',
        level: 10,
        xp: 14.5,
        baseTime: 2500,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 20 }
        ],
        color: '#6b8e23'
    },
    // Gold-focused: More coins, less XP relative to level
    PEDDLER: {
        id: 'peddler',
        name: 'Peddler',
        level: 18,
        xp: 16,
        baseTime: 2800,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 45 }
        ],
        color: '#daa520'
    },
    SELLSWORD: {
        id: 'sellsword',
        name: 'Sellsword',
        level: 25,
        xp: 26,
        baseTime: 3000,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 40 }
        ],
        color: '#8b0000'
    },
    SMUGGLER: {
        id: 'smuggler',
        name: 'Smuggler',
        level: 32,
        xp: 35.5,
        baseTime: 3500,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 50 }
        ],
        color: '#2f4f4f'
    },
    WATCHMAN: {
        id: 'watchman',
        name: 'Watchman',
        level: 40,
        xp: 46.8,
        baseTime: 4000,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 70 }
        ],
        color: '#4169e1'
    },
    // XP-focused: More XP, less coins relative to level
    GUILDMASTER: {
        id: 'guildmaster',
        name: 'Guildmaster',
        level: 48,
        xp: 85,
        baseTime: 4500,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 55 }
        ],
        color: '#1a1a2e'
    },
    SWORN_BLADE: {
        id: 'sworn_blade',
        name: 'Sworn Blade',
        level: 55,
        xp: 84.3,
        baseTime: 5000,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 85 }
        ],
        color: '#c0c0c0'
    },
    // Gold-focused: High coins, moderate XP
    MAGISTRATE: {
        id: 'magistrate',
        name: 'Magistrate',
        level: 62,
        xp: 95,
        baseTime: 5500,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 150 }
        ],
        color: '#800080'
    },
    VEILWARDEN: {
        id: 'veilwarden',
        name: 'Veilwarden',
        level: 70,
        xp: 151.75,
        baseTime: 6000,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 120 }
        ],
        color: '#ffd700'
    },
    EXALTED: {
        id: 'exalted',
        name: 'Exalted',
        level: 80,
        xp: 275,
        baseTime: 7000,
        drops: [
            { itemId: 'coins', weight: 1, quantity: 200 }
        ],
        color: '#9400d3'
    }
};

export const GLOVES = {
    TREATED_HIDE: {
        id: 'treated_hide_gloves',
        name: 'Treated Hide Gloves',
        level: 1,
        speedMultiplier: 1.0,
        price: 0
    },
    STUDDED_HIDE: {
        id: 'studded_hide_gloves',
        name: 'Studded Hide Gloves',
        level: 15,
        speedMultiplier: 1.2,
        price: 1000
    },
    SHADOW: {
        id: 'shadow_gloves',
        name: 'Shadow Gloves',
        level: 30,
        speedMultiplier: 1.4,
        price: 5000
    },
    SERPENT: {
        id: 'serpent_gloves',
        name: 'Serpent Gloves',
        level: 45,
        speedMultiplier: 1.6,
        price: 25000
    },
    WYRM: {
        id: 'wyrm_gloves',
        name: 'Wyrm Gloves',
        level: 60,
        speedMultiplier: 1.8,
        price: 100000
    },
    PHANTOM: {
        id: 'phantom_gloves',
        name: 'Phantom Gloves',
        level: 75,
        speedMultiplier: 2.0,
        price: 250000
    }
};

// ==================== PILFERING GUILD ====================

/**
 * Guild unlock requirements
 */
export const GUILD_REQUIREMENTS = {
    pilfering: 30,      // Pilfering level required
    gold: 40000,        // Gold cost to unlock
    questFlag: null     // Placeholder for future quest system
};

/**
 * Pilferer unlock levels (7 total, one per domain)
 */
export const PILFERER_UNLOCK_LEVELS = [1, 15, 30, 45, 60, 75, 90];

/**
 * Guild chest configuration
 *
 * BALANCE REFERENCE:
 * - Base: 36 unique items from all 7 domains
 * - With all tier upgrades: 92 unique items possible
 * - Slots: 8 base → 24 max (scales with Architecting level)
 * - Stack: 25 base → 200 max (via Guild Vault milestones + 6% speed)
 * - Max capacity: 24 slots × 200 stack = 4,800 items
 * - Architect's Sanctum (L99) auto-loots to bank (seeds to BloomHold first)
 */
export const GUILD_CHEST_CONFIG = {
    BASE_SLOTS: 8,               // Starting chest slots
    SLOTS_PER_10_LEVELS: 2,      // +2 slots per 10 architecting levels
    MAX_SLOTS: 24,               // Cap at 24 slots (covers 36 possible items with some stacking)
    XP_PER_CLAIM_BASE: 50,       // Base Architecting XP per item claimed (scales with Arch level)
    FULL_CHEST_SLOWDOWN: 0.60    // 60% speed reduction when chest full at 99 arch
};

/**
 * Pilferer speed configuration
 * Pilferers are always slower than the player to keep them supplementary
 */
export const PILFERER_SPEED_CONFIG = {
    BASE_SLOWDOWN_PERCENT: 50,   // 50% slower at level 1
    MIN_SLOWDOWN_PERCENT: 25,    // 25% slower at level 99
    BASE_GATHER_TIME: 150000     // 150 seconds base per item (~18-20 min chest fill at mid levels)
};

/**
 * Pilferer XP configuration
 * Awards small Pilfering XP when pilferers complete actions
 * Scales with pilfering level: BASE + (level * SCALE_PER_LEVEL)
 * At level 1: 5 XP, at level 99: ~18 XP per pilferer action
 */
export const PILFERER_XP_CONFIG = {
    BASE_XP: 5,                  // Base XP at level 1
    SCALE_PER_LEVEL: 0.13        // Additional XP per pilfering level (~13 XP bonus at level 99)
};

/**
 * Contract assistance configuration
 */
export const CONTRACT_ASSIST_CONFIG = {
    MIN_PROC_CHANCE: 0.01,       // 1% at level 1
    MAX_PROC_CHANCE: 0.05,       // 5% at level 99
    MIN_PROGRESS_REDUCTION: 0.01, // 1% reduction at level 1
    MAX_PROGRESS_REDUCTION: 0.02  // 2% reduction at level 99
};

/**
 * Guild domains - each domain has a set of lower-tier items pilferers can gather
 * Items are restricted to lower half of tiers to keep pilferers supplementary
 */
export const GUILD_DOMAINS = {
    MINING: {
        id: 'mining',
        name: 'Mining',
        skillIds: ['mining'],  // Skills this domain benefits
        description: 'Gather ores and gems from mining contacts',
        color: '#b87333',
        contractCategory: 'gathering',
        // Lower-half tier items only (4 of 8 ores, 3 of 6 gems)
        items: [
            'pyrestone_ore', 'pale_shard_ore', 'blackiron_ore', 'deepcoal',
            'sapphire', 'emerald', 'ruby'
        ]
    },
    SMITHING: {
        id: 'smithing',
        name: 'Smithing',
        skillIds: ['smithing'],
        description: 'Acquire metal bars from smithing guilds',
        color: '#696969',
        contractCategory: 'processing',
        // Lower-half tier items only (4 of 7 bars)
        items: [
            'gold_bar', 'pyrestone_bar', 'pale_shard_bar', 'blackiron_bar'
        ]
    },
    TEXTILES: {
        id: 'textiles',
        name: 'Textiles',
        skillIds: ['crafting'],  // Cloth used for magic armor crafting
        description: 'Obtain cloth and thread from textile merchants',
        color: '#F5F5DC',
        contractCategory: 'processing',
        // Lower-half tier items only (4 of 6 cloths + spun fiber)
        items: [
            'linenweave', 'silkweave', 'aetherweave', 'runebound_cloth',
            'spun_fiber'
        ]
    },
    HUNTING: {
        id: 'hunting',
        name: 'Hunting',
        skillIds: ['combat', 'pyre'],  // Hides for armor, bones for pyre
        description: 'Gather hides and bones from hunters (combat intel)',
        color: '#8B4513',
        contractCategory: 'kill',
        // Lower-half tier items only (3 of 6 hides, 2 of 5 bones, feathers)
        items: [
            'treated_hide', 'green_wyrm_hide', 'blue_wyrm_hide',
            'small_bone', 'charred_bone',
            'feathers'
        ]
    },
    FOODS: {
        id: 'foods',
        name: 'Foods',
        skillIds: ['fishing', 'cooking'],  // Fish caught, then cooked
        description: 'Source raw food items from fishermen and farmers',
        color: '#fa8072',
        contractCategory: 'gathering',
        // Lower-half tier items only (4 of 9 raw fish, 4 of 9 crops)
        items: [
            'raw_sand_smelt', 'raw_silver_sprat', 'raw_blueback', 'raw_river_char',
            'potato', 'onion', 'cabbage', 'tomato'
        ]
    },
    SEEDS: {
        id: 'seeds',
        name: 'Seeds',
        skillIds: ['farming'],  // Seeds planted in farm
        description: 'Collect farming seeds from agricultural contacts',
        color: '#6b8e23',
        contractCategory: 'gathering',
        // Lower-half tier items only (4 of 9 crop seeds, 2 of 5 tree seeds, 3 of 13 herb seeds)
        items: [
            'potato_seed', 'onion_seed', 'cabbage_seed', 'tomato_seed',
            'ironwood_seed', 'softbark_seed',
            'greenleaf_seed', 'duskleaf_seed', 'ironbloom_seed'
        ]
    },
    GLYPHS: {
        id: 'glyphs',
        name: 'Glyphs',
        skillIds: ['glyphbinding'],  // Glyphs used in glyphbinding
        description: 'Procure glyphs and residue from arcane sources',
        color: '#9932cc',
        contractCategory: 'processing',
        // Lower-half tier items only (2 of 3 essence, 6 of 12 glyphs)
        items: [
            'arcane_residue', 'refined_arcane_ichor',
            'storm_glyph', 'frost_glyph', 'stone_glyph', 'flame_glyph', 'vigor_glyph', 'verdant_glyph'
        ]
    }
};

/**
 * Domain tier upgrade definitions
 * Each tier adds items to the domain's loot pool (cumulative)
 * Unlocked via Pilferer's Network I/II/III architecting milestones
 *
 * Tier 1: Pilferer's Network I (Arch 40, Pilf 40)
 * Tier 2: Pilferer's Network II (Arch 60, Pilf 60)
 * Tier 3: Pilferer's Network III (Arch 80, Pilf 80)
 */
export const DOMAIN_TIER_UPGRADES = {
    mining: {
        1: ['gold_ore'],
        2: ['skyiron_ore'],
        3: ['verdant_core_ore', 'voidsteel_ore']
    },
    smithing: {
        1: ['skyiron_bar'],
        2: ['verdant_core_bar'],
        3: ['voidsteel_bar']
    },
    textiles: {
        1: ['shadowweave'],
        2: ['voidweave'],
        3: []  // Maxed at tier 2
    },
    hunting: {
        // Hides + Bones combined
        1: ['red_wyrm_hide', 'dragon_bone'],
        2: ['black_drake_hide', 'void_bone'],
        3: ['void_drake_hide', 'elder_bone']
    },
    foods: {
        // Fish + Crops combined
        1: ['raw_crimson_char', 'raw_skipjack', 'corn', 'strawberry'],
        2: ['raw_spiny_lobster', 'watermelon'],
        3: ['raw_broadbill', 'raw_mako', 'starbloom', 'kings_fruit']
    },
    seeds: {
        // Crop seeds + Tree seeds + Herb seeds combined
        1: ['corn_seed', 'strawberry_seed', 'goldleaf_seed', 'emberroot_seed', 'sanctum_leaf_seed', 'sunpetal_seed'],
        2: ['watermelon_seed', 'redgrain_seed', 'deepvine_seed', 'stonebark_seed', 'brightmaw_seed'],
        3: ['starbloom_seed', 'kings_fruit_seed', 'gravewood_seed', 'nightvine_seed', 'glowstem_seed', 'ashbloom_seed', 'crownwort_seed']
    },
    glyphs: {
        // Essence + Glyphs combined
        1: ['entropy_glyph', 'void_glyph'],
        2: ['purified_arcane_core', 'binding_glyph', 'doom_glyph'],
        3: ['sanguine_glyph', 'spirit_glyph']
    }
};

/**
 * Get all items available for a domain at a given tier level
 * @param {string} domainId - The domain identifier (e.g., 'mining')
 * @param {number} tierLevel - Current tier level (0 = base, 1-3 = upgrades)
 * @returns {string[]} Array of all available item IDs
 */
export function getDomainItemsAtTier(domainId, tierLevel = 0) {
    const domain = Object.values(GUILD_DOMAINS).find(d => d.id === domainId);
    if (!domain) return [];

    // Start with base items
    const items = [...domain.items];

    // Add tier upgrades cumulatively
    const upgrades = DOMAIN_TIER_UPGRADES[domainId];
    if (upgrades) {
        for (let tier = 1; tier <= tierLevel; tier++) {
            if (upgrades[tier]) {
                items.push(...upgrades[tier]);
            }
        }
    }

    return items;
}

export default {
    TARGETS,
    GLOVES,
    GUILD_REQUIREMENTS,
    PILFERER_UNLOCK_LEVELS,
    GUILD_CHEST_CONFIG,
    PILFERER_SPEED_CONFIG,
    PILFERER_XP_CONFIG,
    CONTRACT_ASSIST_CONFIG,
    GUILD_DOMAINS,
    DOMAIN_TIER_UPGRADES,
    getDomainItemsAtTier
};
